import { ChevronDown, ChevronUp, FileStack, Lock, Pencil, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { GC_TYPE_LABELS, GcLineItemDialog } from "@/components/gc-line-items/GcLineItemDialog";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  useApplyGcLineItemTemplate,
  useGcLineItemTemplates,
} from "@/hooks/use-gc-line-item-templates";
import {
  type GcLineItem,
  type GcLineItemInput,
  useCreateGcLineItem,
  useDeleteGcLineItem,
  useProjectGcLineItems,
  useReorderGcLineItems,
  useUpdateGcLineItem,
} from "@/hooks/use-gc-line-items";

function formatUSD(value: string | number | null | undefined): string {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

export function GcCostSetupTab({ projectId }: { projectId: string }) {
  const { data: rows, isLoading, isError, error, refetch } = useProjectGcLineItems(projectId);
  const create = useCreateGcLineItem(projectId);
  const update = useUpdateGcLineItem(projectId);
  const remove = useDeleteGcLineItem(projectId);
  const reorder = useReorderGcLineItems(projectId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<GcLineItem | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  const totals = useMemo(() => {
    const list = rows ?? [];
    return {
      count: list.length,
      budget: list.reduce((acc, r) => acc + Number(r.budgetAmount ?? 0), 0),
      ownerFacing: list.filter((r) => r.ownerFacing).length,
      locked: list.filter((r) => r.budgetLockedAt).length,
    };
  }, [rows]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load GC line items"}
        onRetry={() => refetch()}
      />
    );
  }

  const list = rows ?? [];

  function openCreate() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(row: GcLineItem) {
    if (row.budgetLockedAt) {
      toast.info("This baseline is locked. Unlock it first from the row menu (Finance only).");
      return;
    }
    setEditing(row);
    setDialogOpen(true);
  }

  async function handleSubmit(input: GcLineItemInput) {
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, updates: input });
        toast.success("GC line item updated");
      } else {
        await create.mutateAsync(input);
        toast.success("GC line item added");
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save line item");
    }
  }

  async function handleDelete(id: string) {
    try {
      await remove.mutateAsync(id);
      toast.success("GC line item deleted");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete line item");
    } finally {
      setConfirmDeleteId(null);
    }
  }

  async function move(row: GcLineItem, delta: number) {
    const idx = list.findIndex((r) => r.id === row.id);
    if (idx < 0) return;
    const target = idx + delta;
    if (target < 0 || target >= list.length) return;
    const next = list.slice();
    const [moved] = next.splice(idx, 1);
    if (!moved) return;
    next.splice(target, 0, moved);
    try {
      await reorder.mutateAsync(next.map((r) => r.id));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to reorder");
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">GC Cost Setup</h2>
          <p className="text-sm text-gray-500">
            Project-level budget for GC-owned cost lines. Amounts here seed each owner pay app
            period and feed the AIA G702 / G703 output.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setTemplateDialogOpen(true)}>
            <FileStack className="mr-1.5 h-4 w-4" />
            Apply template
          </Button>
          <Button onClick={openCreate} size="sm">
            <Plus className="mr-1.5 h-4 w-4" />
            Add GC line item
          </Button>
        </div>
      </div>

      {list.length > 0 ? (
        <div className="grid grid-cols-4 gap-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
          <Stat label="Lines" value={String(totals.count)} />
          <Stat label="Total budget" value={formatUSD(totals.budget)} />
          <Stat label="Owner-facing" value={`${totals.ownerFacing} / ${totals.count}`} />
          <Stat label="Locked" value={`${totals.locked} / ${totals.count}`} />
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
            <tr>
              <th className="px-3 py-2 w-10"></th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Label</th>
              <th className="px-3 py-2">Billing method</th>
              <th className="px-3 py-2 text-right">Budget</th>
              <th className="px-3 py-2">Flags</th>
              <th className="px-3 py-2 w-32 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {list.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                  No GC line items yet. Click "Add GC line item" to seed the Fee, General
                  Conditions, and other GC-owned budgets for this project.
                </td>
              </tr>
            ) : (
              list.map((row, idx) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-2 py-2 align-top">
                    <div className="flex flex-col gap-0.5">
                      <button
                        type="button"
                        aria-label="Move up"
                        disabled={idx === 0}
                        onClick={() => move(row, -1)}
                        className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        aria-label="Move down"
                        disabled={idx === list.length - 1}
                        onClick={() => move(row, 1)}
                        className="text-gray-400 hover:text-gray-700 disabled:opacity-30"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium text-slate-800">{GC_TYPE_LABELS[row.lineType]}</div>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium text-slate-800">{row.label}</div>
                    {row.notes ? <div className="text-xs text-gray-500">{row.notes}</div> : null}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {row.billingMethod === "fixed_lump_sum"
                      ? "Fixed lump sum"
                      : row.billingMethod === "percent_of_hard_costs"
                        ? `Percent of hard costs${row.percentOfHardCosts ? ` (${(Number(row.percentOfHardCosts) * 100).toFixed(2)}%)` : ""}`
                        : "Manual per period"}
                  </td>
                  <td className="px-3 py-2 align-top text-right tabular-nums">
                    {formatUSD(row.budgetAmount)}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex flex-wrap gap-1">
                      {row.budgetLockedAt ? (
                        <Badge tone="red" icon={<Lock className="h-3 w-3" />}>
                          Locked
                        </Badge>
                      ) : null}
                      {!row.ownerFacing ? <Badge tone="amber">Internal only</Badge> : null}
                      {!row.retainageApplies ? <Badge tone="gray">No retainage</Badge> : null}
                      {row.requiresReasonPerDraw ? (
                        <Badge tone="blue">Reason required</Badge>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top text-right">
                    <TooltipProvider>
                      <div className="flex justify-end gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => openEdit(row)}
                              aria-label="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => setConfirmDeleteId(row.id)}
                              aria-label="Delete"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Delete</TooltipContent>
                        </Tooltip>
                      </div>
                    </TooltipProvider>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <GcLineItemDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        existing={editing}
        onSubmit={handleSubmit}
        isPending={create.isPending || update.isPending}
      />

      <ConfirmDialog
        open={confirmDeleteId !== null}
        onOpenChange={(o) => !o && setConfirmDeleteId(null)}
        title="Delete GC line item?"
        description="This will remove the baseline from the project. It cannot be deleted if any pay application has drawn against it."
        confirmLabel="Delete"
        onConfirm={() => {
          if (confirmDeleteId) handleDelete(confirmDeleteId);
        }}
        variant="destructive"
      />

      <ApplyTemplateDialog
        open={templateDialogOpen}
        onOpenChange={setTemplateDialogOpen}
        projectId={projectId}
      />
    </div>
  );
}

function ApplyTemplateDialog({
  open,
  onOpenChange,
  projectId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  projectId: string;
}) {
  const { data: templates, isLoading } = useGcLineItemTemplates();
  const apply = useApplyGcLineItemTemplate();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const activeTemplates = (templates ?? []).filter((t) => t.isActive);
  const selected = activeTemplates.find((t) => t.id === selectedId) ?? null;

  async function handleApply() {
    if (!selectedId) return;
    try {
      const result = await apply.mutateAsync({ templateId: selectedId, projectId });
      toast.success(
        `Template applied — ${result.inserted} line item${result.inserted === 1 ? "" : "s"} added`,
      );
      onOpenChange(false);
      setSelectedId(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to apply template");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Apply GC line item template</DialogTitle>
          <DialogDescription>
            Rows from the template are appended to this project with budget amount $0 — set amounts
            after applying.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : activeTemplates.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No active templates available. Ask an admin to create one in System {"→"}{" "}
              Configuration {"→"} GC Line Item Templates.
            </p>
          ) : (
            <Select value={selectedId ?? ""} onValueChange={setSelectedId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a template..." />
              </SelectTrigger>
              <SelectContent>
                {activeTemplates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                    {t.isDefault ? " · Default" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {selected ? (
            <div className="rounded-lg border bg-muted/30 p-3 text-xs">
              {selected.description ? (
                <p className="mb-2 text-muted-foreground">{selected.description}</p>
              ) : null}
              <p className="font-semibold text-slate-800">
                {selected.rows.length} line item{selected.rows.length === 1 ? "" : "s"}
              </p>
              <ul className="mt-1 list-inside list-disc text-muted-foreground">
                {selected.rows.slice(0, 5).map((r) => (
                  <li key={r.id}>{r.label}</li>
                ))}
                {selected.rows.length > 5 ? <li>…and {selected.rows.length - 5} more</li> : null}
              </ul>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!selectedId || apply.isPending}>
            {apply.isPending ? "Applying…" : "Apply template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-gray-400">{label}</div>
      <div className="text-base font-semibold text-slate-800 tabular-nums">{value}</div>
    </div>
  );
}

function Badge({
  children,
  tone,
  icon,
}: {
  children: React.ReactNode;
  tone: "red" | "amber" | "gray" | "blue";
  icon?: React.ReactNode;
}) {
  const cls = {
    red: "bg-red-50 text-red-700 border-red-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    gray: "bg-gray-100 text-gray-600 border-gray-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
  }[tone];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`}
    >
      {icon}
      {children}
    </span>
  );
}
